import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Divider,
  useTheme,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import { fetchDocumentation } from '../services/apiService';
import { WalteroCard, WalteroTypography, WalteroButton } from './waltero';

const DocumentationTab = () => {
  const theme = useTheme();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDocumentation = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetchDocumentation();

      if (response.success && response.data) {
        setDocuments(response.data.documents || []);
      } else {
        setError('Failed to load documentation');
      }
    } catch (err) {
      console.error('Error loading documentation:', err);
      setError(err.message || 'Failed to load documentation. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDocumentation();
  }, [loadDocumentation]);

  const handleDownload = (document) => {
    // Open the PDF URL in a new tab to trigger download
    window.open(document.url, '_blank');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Product Documentation Section */}
      <WalteroCard sx={{ mb: 3 }}>
        <Box sx={{ p: 3 }}>
          <WalteroTypography
            variant="h6"
            sx={{
              mb: 2,
              color: theme.palette.waltero.primary.coreBlue,
              fontWeight: 600
            }}
          >
            Product Documentation
          </WalteroTypography>

          {documents.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="textSecondary">
                No documentation available at this time
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {documents.map((doc, index) => (
                <React.Fragment key={doc.key}>
                  {index > 0 && <Divider />}
                  <ListItem
                    sx={{
                      py: 2,
                      px: 0,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        cursor: 'pointer',
                      },
                      borderRadius: 1,
                      transition: 'background-color 0.2s',
                    }}
                    onClick={() => handleDownload(doc)}
                  >
                    <ListItemIcon>
                      <PictureAsPdfIcon
                        sx={{
                          color: theme.palette.error.main,
                          fontSize: 40
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <WalteroTypography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          {doc.filename}
                        </WalteroTypography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                          <Typography variant="caption" color="textSecondary">
                            {formatFileSize(doc.size)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Last updated: {formatDate(doc.lastModified)}
                          </Typography>
                        </Box>
                      }
                    />
                    <WalteroButton
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(doc);
                      }}
                      aria-label={`Download ${doc.filename}`}
                    >
                      DOWNLOAD
                    </WalteroButton>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </WalteroCard>

      {/* Help Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          backgroundColor: theme.palette.waltero.secondary.backgroundApricot,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <WalteroTypography variant="h6" sx={{ mb: 1 }}>
          Need Additional Help?
        </WalteroTypography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          If you can't find what you're looking for, please contact our support team.
        </Typography>
        <WalteroButton
          variant="contained"
          onClick={() => {
            window.location.href = 'mailto:support@waltero.com?subject=Support request';
          }}
        >
          CONTACT SUPPORT
        </WalteroButton>
      </Paper>
    </Box>
  );
};

export default DocumentationTab;
